// Types and interfaces
export interface SortOption {
  label: string
  value: string
}

export interface FilterQueryProperty {
  in_array?: string
  in?: string
  any_in_array?: string
  gt_date?: string
  is?: string
  not_in?: string
}

export interface FilterQuery {
  filter_query: {
    topics?: FilterQueryProperty
    states?: FilterQueryProperty
    audiences?: FilterQueryProperty
    type?: FilterQueryProperty
    service_type?: FilterQueryProperty
    date_end?: FilterQueryProperty
    date_start?: FilterQueryProperty
    __or?: Array<{
      date_end?: FilterQueryProperty
      date_start?: FilterQueryProperty
    }>
    _uid?: FilterQueryProperty
  }
}

export interface SortQuery {
  sort_by: string
}

export interface QueryParams {
  topics?: string | string[]
  states?: string | string[]
  audiences?: string | string[]
  'resource-type'?: string
  'service-type'?: string
  sort_by?: string
  ignoreAllOption?: boolean
}

export default class DefaultQueryFormatter {
  /**
   * Get the filters available for this type,
   * along with any defaults to apply when first loaded
   */
  getDefaultFilters(_params: QueryParams = {}): {
    states: string[]
    audiences: string[]
    topics: string[]
  } {
    return {
      states: [],
      audiences: [],
      topics: [],
    }
  }

  /**
   * Get the default sort to apply when loaded
   * Should map to the `value` of an entry the in the return of `getSortOptions`
   */
  getDefaultSort(params: QueryParams = {}): string {
    return this.getSortOptions(params)[0].value
  }

  /**
   * Get the sort options available for this type.
   */
  getSortOptions(_params: QueryParams = {}): SortOption[] {
    return [
      { label: 'Most Recent', value: 'published_at:desc' },
      { label: 'Oldest', value: 'published_at:asc' },
      { label: 'A–Z', value: 'name:asc' },
      { label: 'Z–A', value: 'name:desc' },
    ]
  }

  /**
   * Converts the given URL params into a Storyblok query filter object
   */
  getFilterQuery(params: QueryParams = {}): FilterQuery {
    const filterQuery: FilterQuery['filter_query'] = {}

    // filter by topics
    if (params.topics) {
      filterQuery.topics = {
        any_in_array: [params.topics].flat().join(','),
      }
    }

    // filter by states
    // adding a hidden ...',national' to display National content & selected states
    if (params.states) {
      filterQuery.states = {
        any_in_array: params.states.includes('national')
          ? [params.states].flat().join(',')
          : [params.states].flat().join(',') + ',national',
      }
    }

    // filter by audiences
    // optionally ignore including "all" when looking up content
    if (params.audiences) {
      console.log('params.audiences', params.audiences)
      filterQuery.audiences = {
        any_in_array:
          params.audiences.includes('all') || params.ignoreAllOption
            ? [params.audiences].flat().join(',')
            : [params.audiences].flat().join(',') + ',all',
      }
    }

    // filter by resource-type (singular)
    if (params['resource-type']) {
      filterQuery.type = { in: params['resource-type'] }
    }

    // filter by service-type
    if (params['service-type']) {
      filterQuery.service_type = { any_in_array: params['service-type'] }
    }

    return {
      filter_query: filterQuery,
    }
  }

  /**
   * Converts the given query params into a Storyblok sort query object
   */
  getSortQuery(params: QueryParams = {}): SortQuery {
    const sort = params?.sort_by
    const opt = this.getSortOptions(params).find((option) => {
      return option.value === sort
    })
    const res = opt?.value || this.getDefaultSort()
    return {
      sort_by: res,
    }
  }
}
